import React, { useEffect, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { Download, Upload, XCircle, RotateCcw, FileText, BadgeCheck, Link2, AlertTriangle, FileUp, Ban } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import moment from 'moment';
import SettingsModal from '../../Settings/SettingsModal';
import { EmptyState } from '../../Common/EmptyState';
import StatusBadge from '../../Common/StatusBadge';
import { apiError, cancelFile, cancelRemainder, downloadFileContent, fetchFile, linkRowPayment, markFilePaid, markRowPaid, markUploaded, retryInstructions, uploadReturnFile } from './paymentsApi';
import { CATEGORY, EVENT_LABEL, FILE_STATUS, ROW_STATUS, SCHEME_LABEL, kr } from './paymentStatus';

// Whole-file "Avbryt". A partly debited file (partially_settled) is not offered
// it: "Avbryt resterande betalningar" cancels what is left and keeps the file
// truthful about the rows the bank already paid.
const OPEN_FOR_CANCEL = ['created', 'uploaded', 'accepted', 'partially_accepted'];
const SENT_ROW = ['in_file', 'pending', 'accepted'];
const MARK_PAID_FILE = ['uploaded', 'accepted', 'partially_accepted', 'partially_settled'];
// Today in the user's own time zone. toISOString() is UTC, which is still
// yesterday during the first hours of a Swedish day.
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * "Markera som betald" (plan §3.11 intro): registers the payment on the
 * invoice with the row's exact amount and the instruction id, so the row is
 * settled and nothing is orphaned when the bank line arrives in session 3.
 * Supplier rows only; Skatteverket and manual rows settle from the bank line.
 */
const MarkPaidDialog = ({ show, file, row, companyId, onClose, onDone }) => {
  const [date, setDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (show) setDate(file?.requested_execution_date && file.requested_execution_date <= todayIso() ? file.requested_execution_date : todayIso()); }, [show, file]);
  const supplierRows = (file?.rows || []).filter((r) => r.source_type === 'supplier_invoices' && SENT_ROW.includes(r.status));
  const targets = row ? [row] : supplierRows;
  const total = targets.reduce((s, r) => s + Number(r.amount), 0);

  const submit = async () => {
    setBusy(true);
    try {
      const result = row
        ? await markRowPaid(companyId, file.batch_id, row.instruction_id, { payment_date: date })
        : await markFilePaid(companyId, file.batch_id, { payment_date: date });
      if (row) toast.success(`${row.creditor_name} markerad som betald.`);
      else {
        toast.success(`${result.settled.length} betalningar registrerade på fakturorna.`);
        for (const sk of result.skipped || []) if (!/bankhändelsen/.test(sk.reason)) toast.warning(`${sk.label}: ${sk.reason}`);
      }
      onDone(result.file);
    } catch (err) {
      toast.error(apiError(err, 'Kunde inte markera som betald.').message);
    } finally {
      setBusy(false);
    }
  };
  const footer = (
    <>
      <button type="button" className="ll-btn ll-btn--secondary" onClick={onClose} disabled={busy}>Avbryt</button>
      <button type="button" className="ll-btn ll-btn--primary" onClick={submit} disabled={busy || targets.length === 0}>{busy ? 'Registrerar…' : 'Markera som betald'}</button>
    </>
  );
  return (
    <SettingsModal show={show} onHide={onClose} title={row ? 'Markera raden som betald' : 'Markera filen som betald'} footer={footer}>
      <p style={{ fontSize: 13 }}>
        {row ? <>Betalningen av <strong>{row.invoice?.invoice_number || row.creditor_name}</strong> ({kr(row.amount)})</> : <>{targets.length} leverantörsfakturor ({kr(total)})</>} registreras på fakturan med exakt radens belopp, bokförs mot kontot filen dras från och kopplas till betalfilen.
        {!row && (file?.rows || []).some((r) => SENT_ROW.includes(r.status) && r.source_type !== 'supplier_invoices') ? ' Skatt och manuella betalningar bokförs när bankhändelsen stäms av.' : ''}
      </p>
      <Form.Group>
        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Betalningsdatum</Form.Label>
        <Form.Control type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
      </Form.Group>
      <p className="text-muted" style={{ fontSize: 12, marginTop: 10 }}>En faktura med kontantmetoden behöver kontering och registreras från fakturan; den kopplas då till filen automatiskt.</p>
    </SettingsModal>
  );
};

/**
 * "Ladda upp återrapport" (plan §3.11.1): the bank's pain.002 status report or
 * camt.054 debit notification, fetched in the internet bank. Optional — a
 * company that never uploads one loses nothing. pain.002 updates row statuses;
 * camt.054 becomes a bank line with the file's references and is matched like
 * any other line (Bankhändelser).
 */
const ReturnFileDropZone = ({ companyId, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef(null);
  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const result = await uploadReturnFile(companyId, file);
      if (result.type === 'pain.002') {
        if (result.unknown_file) toast.warning('Återrapporten gäller en okänd fil (meddelande-id matchar ingen betalfil).');
        else if (result.duplicate_report) toast.info('Återrapporten är redan inläst.');
        else {
          const parts = [`${result.updates.length} rad${result.updates.length === 1 ? '' : 'er'} uppdaterade`];
          if (result.duplicates.length) parts.push(`${result.duplicates.length} dubbel uppladdning rapporterad`);
          if (result.contradictory.length) parts.push(`${result.contradictory.length} motstridig status`);
          if (result.uploaded_now) parts.push('filen markerad som uppladdad');
          toast.success(`Återrapport inläst för ${result.label}: ${parts.join(', ')}.`);
        }
      } else {
        const c = result.counts || {};
        toast.success(`${result.type} inläst: ${c.imported || 0} ny bankhändelse${c.imported === 1 ? '' : 'r'}, ${c.duplicates || 0} redan kända. Bankhändelserna matchas mot betalfilerna.`);
      }
      onDone(result);
    } catch (err) {
      toast.error(apiError(err, 'Återrapporten kunde inte läsas in.').message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); send(e.dataTransfer.files?.[0]); }}
      onClick={() => !busy && input.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') input.current?.click(); }}
      style={{ border: `1px dashed ${over ? '#2563eb' : '#cbd5e1'}`, background: over ? '#eff6ff' : '#f8fafc', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#475569', cursor: busy ? 'wait' : 'pointer', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}
    >
      <FileUp size={14} />
      <span>
        <strong>Ladda upp återrapport</strong> från internetbanken (pain.002 statusrapport eller camt.054 debiteringsavi, XML). {busy ? 'Läser in…' : 'Släpp filen här eller klicka.'}
      </span>
      <input ref={input} type="file" accept=".xml,application/xml,text/xml" style={{ display: 'none' }} onChange={(e) => send(e.target.files?.[0])} />
    </div>
  );
};

/** "Avbryt resterande betalningar" (plan §3.11.5): attested cancellation of the outstanding rows only. */
const CancelRemainderDialog = ({ show, file, companyId, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(''); }, [show]);
  const outstanding = (file?.rows || []).filter((r) => SENT_ROW.includes(r.status));
  const submit = async () => {
    setBusy(true);
    try {
      const updated = await cancelRemainder(companyId, file.batch_id, reason);
      toast.success(`${outstanding.length} resterande betalningar avbrutna.`);
      onDone(updated);
    } catch (err) {
      toast.error(apiError(err, 'Kunde inte avbryta betalningarna.').message);
    } finally {
      setBusy(false);
    }
  };
  const footer = (
    <>
      <button type="button" className="ll-btn ll-btn--secondary" onClick={onClose} disabled={busy}>Stäng</button>
      <button type="button" className="ll-btn ll-btn--danger" onClick={submit} disabled={busy || !reason.trim()}>{busy ? 'Avbryter…' : 'Avbryt resterande'}</button>
    </>
  );
  return (
    <SettingsModal show={show} onHide={onClose} title="Avbryt resterande betalningar" footer={footer}>
      <p style={{ fontSize: 13 }}>
        {outstanding.length} betalning{outstanding.length === 1 ? '' : 'ar'} i <strong>{file?.label}</strong> har inte debiterats. Avbryt dem först i banken; betalda rader rörs inte. Därefter kan de göras om via "Försök igen".
      </p>
      <Form.Group>
        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Bankens referens eller hur betalningarna stoppades</Form.Label>
        <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} placeholder="T.ex. ärende 4711 hos banken" />
      </Form.Group>
    </SettingsModal>
  );
};

/** Cancel dialog with the attestation the backend requires once a file was downloaded or uploaded. */
const CancelFileDialog = ({ show, file, companyId, onClose, onDone }) => {
  const needsAttestation = Boolean(file?.downloaded_at || file?.uploaded_at);
  const uploaded = Boolean(file?.uploaded_at);
  const [attestation, setAttestation] = useState(uploaded ? 'stopped_at_bank' : 'not_uploaded');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setAttestation(uploaded ? 'stopped_at_bank' : 'not_uploaded'); setReason(''); }, [show, uploaded]);

  const submit = async () => {
    setBusy(true);
    try {
      const updated = await cancelFile(companyId, file.batch_id, { attestation: needsAttestation ? attestation : null, reason: reason || null });
      toast.success('Betalfilen är avbruten.');
      onDone(updated);
    } catch (err) {
      toast.error(apiError(err, 'Kunde inte avbryta filen.').message);
    } finally {
      setBusy(false);
    }
  };

  const referenceRequired = needsAttestation && attestation === 'stopped_at_bank' && !reason.trim();
  const footer = (
    <>
      <button type="button" className="ll-btn ll-btn--secondary" onClick={onClose} disabled={busy}>Stäng</button>
      <button type="button" className="ll-btn ll-btn--danger" onClick={submit} disabled={busy || referenceRequired}>{busy ? 'Avbryter…' : 'Avbryt betalfilen'}</button>
    </>
  );

  return (
    <SettingsModal show={show} onHide={onClose} title="Avbryt betalfil" footer={footer}>
      {file && (
        <>
          <p style={{ fontSize: 13 }}>
            <strong>{file.label}</strong> · {file.instruction_count} betalningar · {kr(file.total_amount)}
          </p>
          {needsAttestation ? (
            <>
              <p style={{ fontSize: 13 }}>
                Filen har {uploaded ? 'markerats som uppladdad' : 'laddats ner'}. För att inte riskera dubbla utbetalningar måste du ange vad som gäller:
              </p>
              <Form.Check
                type="radio"
                id="att-not-uploaded"
                name="attestation"
                label="Filen har inte laddats upp till banken"
                checked={attestation === 'not_uploaded'}
                onChange={() => setAttestation('not_uploaded')}
                disabled={uploaded}
              />
              <Form.Check
                type="radio"
                id="att-stopped"
                name="attestation"
                label="Filen är uppladdad och stoppad/makulerad i banken"
                checked={attestation === 'stopped_at_bank'}
                onChange={() => setAttestation('stopped_at_bank')}
              />
              {attestation === 'stopped_at_bank' && (
                <Form.Group style={{ marginTop: 10 }}>
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Bankens referens eller hur filen stoppades</Form.Label>
                  <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} placeholder="T.ex. ärende 4711 hos banken" />
                </Form.Group>
              )}
            </>
          ) : (
            <Form.Group>
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Anledning (valfritt)</Form.Label>
              <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} />
            </Form.Group>
          )}
          <p className="text-muted" style={{ fontSize: 12, marginTop: 10 }}>
            Betalningarna i filen avbryts. De kan göras om via "Försök igen" på filen.
          </p>
        </>
      )}
    </SettingsModal>
  );
};

const FileDetail = ({ companyId, file, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [showRemainder, setShowRemainder] = useState(false);
  const [markPaid, setMarkPaid] = useState(null); // { row } or { row: null } for the whole file
  const status = FILE_STATUS[file.status] || { label: file.status, tone: 'muted' };
  const retryable = file.rows.filter((r) => ['rejected', 'cancelled'].includes(r.status));
  // Session 3: return-flow findings (plan §3.11.2) and the partial-debit way out (§3.11.5).
  const duplicateReports = file.events.filter((e) => e.event_type === 'duplicate_submission_reported');
  const contradictoryReports = file.events.filter((e) => e.event_type === 'contradictory_report');
  const outstanding = file.rows.filter((r) => SENT_ROW.includes(r.status));
  const settledRows = file.rows.filter((r) => r.status === 'settled');
  const canCancelRemainder = ['uploaded', 'accepted', 'partially_accepted', 'partially_settled'].includes(file.status) && outstanding.length > 0 && (settledRows.length > 0 || file.rows.some((r) => r.status === 'rejected'));
  const eventPayload = (e) => (typeof e.payload === 'string' ? (() => { try { return JSON.parse(e.payload); } catch (_) { return {}; } })() : e.payload || {});
  const canMarkPaid = file.category_purpose === 'SUPP' && MARK_PAID_FILE.includes(file.status) && file.rows.some((r) => r.source_type === 'supplier_invoices' && SENT_ROW.includes(r.status));

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      const result = await fn();
      if (ok) toast.success(ok);
      onChanged(result);
    } catch (err) {
      toast.error(apiError(err).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="modern-card">
      <div className="modern-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{file.label}</div>
          <div className="text-muted" style={{ fontSize: 13 }}>
            {CATEGORY[file.category_purpose]} · {file.instruction_count} betalningar · {kr(file.total_amount)} · dras {file.requested_execution_date}
            {file.payout_date && file.category_purpose === 'SALA' ? ` · utbetalas ${file.payout_date}` : ''}
          </div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            Från {file.debtor_nickname || file.debtor_bank_name || ''} {file.debtor_clearing_number}-{file.debtor_account_number} · {file.file_name}
          </div>
        </div>
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
      </div>
      <div className="modern-card-body">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {file.status !== 'cancelled' && (
            <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" disabled={busy === 'dl'} onClick={() => run('dl', async () => { await downloadFileContent(companyId, file); return fetchFile(companyId, file.batch_id); })}>
              <Download size={14} /> Ladda ner
            </button>
          )}
          {!file.uploaded_at && file.status === 'created' && (
            <button type="button" className="ll-btn ll-btn--primary ll-btn--sm" disabled={busy === 'up'} onClick={() => run('up', () => markUploaded(companyId, file.batch_id), 'Filen är markerad som uppladdad.')}>
              <Upload size={14} /> Markera som uppladdad
            </button>
          )}
          {canMarkPaid && (
            <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={() => setMarkPaid({ row: null })}>
              <BadgeCheck size={14} /> Markera som betald
            </button>
          )}
          {canCancelRemainder && (
            <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" onClick={() => setShowRemainder(true)} title="Avbryt de betalningar som inte debiterats (kräver att de stoppats i banken)">
              <Ban size={14} /> Avbryt resterande betalningar
            </button>
          )}
          {OPEN_FOR_CANCEL.includes(file.status) && (
            <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" onClick={() => setShowCancel(true)}>
              <XCircle size={14} /> Avbryt
            </button>
          )}
          {retryable.length > 0 && (
            <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" disabled={busy === 'retry'} onClick={() => run('retry', async () => { await retryInstructions(companyId, retryable.map((r) => r.instruction_id)); return fetchFile(companyId, file.batch_id); }, `${retryable.length} betalningar lades tillbaka i kön.`)}>
              <RotateCcw size={14} /> Försök igen ({retryable.length})
            </button>
          )}
        </div>

        {file.status !== 'created' && file.status !== 'cancelled' && (
          <ReturnFileDropZone companyId={companyId} onDone={() => fetchFile(companyId, file.batch_id).then(onChanged).catch(() => {})} />
        )}
        {duplicateReports.length > 0 && !['settled', 'cancelled', 'rejected'].includes(file.status) && (
          <div style={{ fontSize: 12, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
            <AlertTriangle size={12} className="me-1" />
            Banken rapporterar att filen verkar ha laddats upp två gånger ({eventPayload(duplicateReports[duplicateReports.length - 1]).code || 'dubblett'}). Kontrollera i banken innan något görs om; raderna behåller sin status tills banken avvisar, debiterar eller filen stoppas.
          </div>
        )}
        {contradictoryReports.length > 0 && (
          <div style={{ fontSize: 12, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
            <AlertTriangle size={12} className="me-1" />
            Banken rapporterar en annan status för en avslutad betalning ({contradictoryReports.map((e) => `${eventPayload(e).row_status} → ${eventPayload(e).reported_status}`).join(', ')}). Kontrollera i banken: en betalning kan ha gått iväg trots att raden är avslutad här.
          </div>
        )}
        {file.uploadChannel && file.status === 'created' && (
          <div style={{ fontSize: 12, color: '#475569', background: '#f8fafc', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
            <strong>Nästa steg:</strong> {file.uploadChannel} Markera sedan filen som uppladdad här.
          </div>
        )}
        {file.method_changed_rows > 0 && file.status !== 'cancelled' && (
          <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
            <AlertTriangle size={12} className="me-1" />
            Betalningsuppgifterna har ändrats för {file.method_changed_rows} leverantör{file.method_changed_rows === 1 ? '' : 'er'} sedan filen skapades. Filen är oförändrad; avbryt den och skapa en ny om betalningen ska gå till de nya uppgifterna.
          </div>
        )}
        {file.cancel_attestation && (
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 12 }}>
            Avbruten: {file.cancel_attestation === 'stopped_at_bank' ? 'stoppad i banken' : 'inte uppladdad'}{file.cancel_reason ? ` — ${file.cancel_reason}` : ''}
          </div>
        )}

        <table className="table table-sm" style={{ fontSize: 13 }}>
          <thead><tr><th>#</th><th>Mottagare</th><th>Konto</th><th className="text-end">Belopp</th><th>Status</th><th>Referens</th><th /></tr></thead>
          <tbody>
            {file.rows.map((r) => {
              const rs = ROW_STATUS[r.status] || { label: r.status, tone: 'muted' };
              const exactOutside = (r.outside_payments || []).find((p) => p.exact);
              const inexactOutside = (r.outside_payments || []).filter((p) => !p.exact);
              const rowMarkable = file.category_purpose === 'SUPP' && MARK_PAID_FILE.includes(file.status) && r.source_type === 'supplier_invoices' && SENT_ROW.includes(r.status);
              return (
                <tr key={r.instruction_id} style={r.method_changed && SENT_ROW.includes(r.status) ? { background: '#fffbeb' } : undefined}>
                  <td>{r.seq}</td>
                  <td>
                    {r.creditor_name}
                    {r.invoice && (
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        <Link to={`/${companyId}/supplier-invoices/${r.invoice.invoice_id}`}>Faktura {r.invoice.invoice_number}</Link>
                        {r.invoice.status === 'Paid' ? ' · betald' : ''}
                      </div>
                    )}
                    {r.method_changed && SENT_ROW.includes(r.status) && <div style={{ fontSize: 12, color: '#92400e' }}>Betalningsuppgifterna har ändrats</div>}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{SCHEME_LABEL[r.creditor_scheme] || r.creditor_scheme} {String(r.creditor_id).length > 4 ? `…${String(r.creditor_id).slice(-4)}` : r.creditor_id}</td>
                  <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{kr(r.amount)}</td>
                  <td>
                    <StatusBadge tone={rs.tone}>{rs.label}</StatusBadge>
                    {r.status_reason_code && <div style={{ fontSize: 12, color: r.status === 'rejected' ? '#991b1b' : '#64748b' }}>{r.status_reason_text || r.status_reason_code}{r.status_reason_text ? ` (${r.status_reason_code})` : ''}</div>}
                    {r.accepted_execution_date && r.accepted_execution_date !== file.requested_execution_date && <div className="text-muted" style={{ fontSize: 12 }}>Banken dras {r.accepted_execution_date}</div>}
                    {r.status === 'settled' && r.settled_statement_line_id && <div className="text-muted" style={{ fontSize: 12 }}><Link to={`/${companyId}/bankstatements?filter=all&line=${r.settled_statement_line_id}`}>Avstämd mot bankhändelse</Link></div>}
                    {r.status === 'settled' && !r.settled_statement_line_id && <div className="text-muted" style={{ fontSize: 12 }}>Betald, inte bankkopplad</div>}
                    {SENT_ROW.includes(r.status) && file.status === 'partially_settled' && <div className="text-muted" style={{ fontSize: 12 }}>Ingen betalning sedd</div>}
                    {inexactOutside.map((p) => (
                      <div key={p.payment_id} style={{ fontSize: 12, color: '#92400e' }}>
                        Betalning registrerad utanför filen ({kr(p.amount)} av {kr(r.amount)}) — koppla eller lämna
                      </div>
                    ))}
                  </td>
                  <td className="text-muted" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                    {r.remittance_type === 'SCOR' ? `OCR ${r.remittance_ref}` : r.remittance_ref || ''}
                    <div>{r.end_to_end_id}</div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {rowMarkable && exactOutside && (
                      <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" disabled={busy === r.instruction_id} title={`Koppla betalningen ${exactOutside.payment_date} (${kr(exactOutside.amount)})`}
                        onClick={() => run(r.instruction_id, async () => (await linkRowPayment(companyId, file.batch_id, r.instruction_id, exactOutside.payment_id)).file, 'Betalningen kopplades till raden.')}>
                        <Link2 size={13} /> Koppla
                      </button>
                    )}
                    {rowMarkable && !exactOutside && (
                      <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" onClick={() => setMarkPaid({ row: r })} title="Registrera betalningen på fakturan med radens belopp">
                        <BadgeCheck size={13} /> Betald
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ fontSize: 12, color: '#64748b' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Händelser</div>
          {file.events.map((e, i) => (
            <div key={i}>{moment(e.created_at).format('YYYY-MM-DD HH:mm')} · {EVENT_LABEL[e.event_type] || e.event_type}{e.actor_type === 'system' ? ' (system)' : ''}</div>
          ))}
        </div>
      </div>

      <CancelFileDialog show={showCancel} file={file} companyId={companyId} onClose={() => setShowCancel(false)} onDone={(f) => { setShowCancel(false); onChanged(f); }} />
      <CancelRemainderDialog show={showRemainder} file={file} companyId={companyId} onClose={() => setShowRemainder(false)} onDone={(f) => { setShowRemainder(false); onChanged(f); }} />
      <MarkPaidDialog show={Boolean(markPaid)} file={file} row={markPaid?.row || null} companyId={companyId} onClose={() => setMarkPaid(null)} onDone={(f) => { setMarkPaid(null); onChanged(f); }} />
    </div>
  );
};

/** Betalfiler — list and detail. */
const PaymentFilesTab = ({ companyId, files, loading, onRefresh, selectedId, onSelect }) => {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    fetchFile(companyId, selectedId)
      .then((f) => !cancelled && setDetail(f))
      .catch((err) => toast.error(apiError(err, 'Kunde inte hämta filen.').message))
      .finally(() => !cancelled && setDetailLoading(false));
    return () => { cancelled = true; };
  }, [companyId, selectedId]);

  if (!loading && files.length === 0) {
    return <EmptyState icon={FileText} title="Inga betalfiler ännu" description="Skapa en betalfil från betalningskön. Filen laddas upp i internetbanken och signeras där." />;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: detail || detailLoading ? 'minmax(280px, 1fr) 2fr' : '1fr', gap: 16, alignItems: 'start' }}>
      <div className="modern-card">
        <div className="modern-card-body" style={{ padding: 0 }}>
          <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
            <thead><tr><th>Fil</th><th>Status</th><th className="text-end">Belopp</th><th>Dras</th></tr></thead>
            <tbody>
              {files.map((f) => {
                const s = FILE_STATUS[f.status] || { label: f.status, tone: 'muted' };
                return (
                  <tr key={f.batch_id} onClick={() => onSelect(f.batch_id)} style={{ cursor: 'pointer', background: f.batch_id === selectedId ? '#f1f5f9' : undefined }}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{f.label}</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>{CATEGORY[f.category_purpose]} · {f.instruction_count} st · {moment(f.created_at).format('YYYY-MM-DD')}</div>
                    </td>
                    <td><StatusBadge tone={s.tone}>{s.label}</StatusBadge></td>
                    <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{kr(f.total_amount)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{f.requested_execution_date}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {detailLoading && !detail ? (
        <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
      ) : detail ? (
        <FileDetail companyId={companyId} file={detail} onChanged={(f) => { setDetail(f); onRefresh(); }} />
      ) : null}
    </div>
  );
};

export default PaymentFilesTab;
